import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Supported currencies with symbols and locales
export const CURRENCIES = {
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', locale: 'en-US' },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', locale: 'de-DE' },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', locale: 'en-GB' },
  CAD: { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', locale: 'en-CA' },
  AUD: { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', locale: 'en-AU' },
  JPY: { code: 'JPY', symbol: '¥', name: 'Japanese Yen', locale: 'ja-JP' },
  CNY: { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', locale: 'zh-CN' },
  INR: { code: 'INR', symbol: '₹', name: 'Indian Rupee', locale: 'en-IN' },
  CHF: { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', locale: 'de-CH' },
  SGD: { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', locale: 'en-SG' },
  NZD: { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar', locale: 'en-NZ' },
  MXN: { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso', locale: 'es-MX' },
  BRL: { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', locale: 'pt-BR' },
  KRW: { code: 'KRW', symbol: '₩', name: 'South Korean Won', locale: 'ko-KR' },
  ZAR: { code: 'ZAR', symbol: 'R', name: 'South African Rand', locale: 'en-ZA' },
  AED: { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', locale: 'ar-AE' },
  SAR: { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal', locale: 'ar-SA' },
  SEK: { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', locale: 'sv-SE' },
  NOK: { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone', locale: 'nb-NO' },
  DKK: { code: 'DKK', symbol: 'kr', name: 'Danish Krone', locale: 'da-DK' },
};

// Format currency with specified currency code
export function formatCurrency(value, currency = 'USD', decimals = 0) {
  if (value === null || value === undefined) return `${CURRENCIES[currency]?.symbol || '$'}0`;
  
  const currencyConfig = CURRENCIES[currency] || CURRENCIES.USD;
  
  try {
    return new Intl.NumberFormat(currencyConfig.locale, {
      style: 'currency',
      currency: currencyConfig.code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  } catch (e) {
    // Fallback for unsupported currencies
    return `${currencyConfig.symbol}${value.toLocaleString()}`;
  }
}

// Get currency symbol only
export function getCurrencySymbol(currency = 'USD') {
  return CURRENCIES[currency]?.symbol || '$';
}

// Format compact currency (e.g., $1.5M, €200K)
export function formatCurrencyCompact(value, currency = 'USD') {
  if (value === null || value === undefined) return `${getCurrencySymbol(currency)}0`;
  
  const symbol = getCurrencySymbol(currency);
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  
  if (absValue >= 1000000000) {
    return `${sign}${symbol}${(absValue / 1000000000).toFixed(1)}B`;
  }
  if (absValue >= 1000000) {
    return `${sign}${symbol}${(absValue / 1000000).toFixed(1)}M`;
  }
  if (absValue >= 1000) {
    return `${sign}${symbol}${(absValue / 1000).toFixed(0)}K`;
  }
  return `${sign}${symbol}${absValue.toFixed(0)}`;
}

// Format percentage
export function formatPercent(value, decimals = 1) {
  if (value === null || value === undefined) return '0%';
  return `${value.toFixed(decimals)}%`;
}

// Format ROI as multiplier (x)
export function formatROI(value, decimals = 1) {
  if (value === null || value === undefined) return '0x';
  return `${(value / 100).toFixed(decimals)}x`;
}

// Format number with commas
export function formatNumber(value, decimals = 0) {
  if (value === null || value === undefined) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

// Get ROI status color (roi is in percentage, e.g., 200 = 2x)
export function getROIStatus(roi) {
  if (roi >= 200) return { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Excellent' };
  if (roi >= 100) return { color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-200', label: 'Strong' };
  if (roi >= 50) return { color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Good' };
  if (roi >= 0) return { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Moderate' };
  return { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Negative' };
}

// Get maturity band
export function getMaturityBand(maturity) {
  if (maturity <= 25) return { label: 'Foundational', color: '#ef4444', description: 'Early stage development' };
  if (maturity <= 50) return { label: 'Developing', color: '#f59e0b', description: 'Building capabilities' };
  if (maturity <= 75) return { label: 'Acceleration', color: '#10b981', description: 'Strong momentum' };
  return { label: 'Optimization', color: '#0d9488', description: 'Diminishing returns territory' };
}

// Download blob as file
export function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// Parse markdown-style text to HTML
export function parseNarrativeToHTML(text) {
  if (!text) return '';
  return text
    // Handle h3 headers (###) first - most specific
    .replace(/^### (.*?)$/gm, '<h3 class="text-lg font-semibold text-slate-800 mt-5 mb-2 uppercase tracking-wide">$1</h3>')
    // Handle h2 headers (##)
    .replace(/^## (.*?)$/gm, '<h2 class="text-xl font-semibold text-slate-900 mt-6 mb-3">$1</h2>')
    // Handle h1 headers (#) - convert to h3 for narrative
    .replace(/^# (.*?)$/gm, '<h3 class="text-lg font-semibold text-slate-800 mt-5 mb-2">$1</h3>')
    // Handle bold text
    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-slate-800">$1</strong>')
    // Handle bullet points
    .replace(/^- (.*?)$/gm, '<li class="ml-4 text-slate-600">$1</li>')
    // Handle double line breaks as paragraph separators
    .replace(/\n\n/g, '</p><p class="mb-4 text-slate-600">')
    // Wrap in paragraphs
    .replace(/^/, '<p class="mb-4 text-slate-600">')
    .replace(/$/, '</p>')
    // Clean up empty paragraphs around headers
    .replace(/<p class="mb-4 text-slate-600"><h/g, '<h')
    .replace(/<\/h2><\/p>/g, '</h2>')
    .replace(/<\/h3><\/p>/g, '</h3>');
}
